/**
 * Secondary stream route page for Sec 1-4 G1/G2/G3/IP combinations.
 *
 * What: Resolves dynamic secondary stream route params into generated level content and renders it via `LevelPageLayout`,
 *       with a stream-filtered school directory at the bottom.
 * Why: Avoids creating 16 near-identical page components while keeping validation and fallback behavior centralized.
 * When: Update when supported secondary stream route patterns or generated secondary content keys change.
 */
import React, { useMemo } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import LevelPageLayout from '@/components/levels/LevelPageLayout.js';
import SchoolDirectory from '@/components/schools/SchoolDirectory.js';
import { toLevelPageLayoutContent } from '@/content/levelPageLayoutAdapter';
import { buildSecondaryStreamContent } from '@/content/levels/secondaryStreamContent';
import { useSchoolDirectory } from '@/hooks/useSchoolDirectory.js';
import {
  filterSecondaryByExam,
  filterSecondaryByStream,
} from '@/utils/schoolFilters.js';

const SEC_LEVELS = new Set(['1', '2', '3', '4']);
const STREAM_KEY_SUFFIX_MAP: Record<string, string> = {
  g1: 'G1',
  g2: 'G2',
  g3: 'G3',
  ip: 'IP',
  ib: 'IB',
};

const STREAM_DIRECTORY_HEADINGS: Record<string, string> = {
  g1: 'Secondary Schools offering G1 (Normal Technical)',
  g2: 'Secondary Schools offering G2 (Normal Academic)',
  g3: 'Secondary Schools offering G3 / IP',
  ip: 'IP Schools in Singapore',
  ib: 'IB Schools in Singapore',
};

interface SecondaryStreamPageProps {
  secLevel?: string;
  stream?: string;
}

function SecondaryStreamPage({
  secLevel: secLevelProp,
  stream: streamProp,
}: SecondaryStreamPageProps) {
  const { secLevel: secLevelParam, stream: streamParam } = useParams();
  const secLevel = secLevelProp ?? secLevelParam;
  const normalizedStream = (streamProp ?? streamParam)?.toLowerCase() ?? '';
  const streamKeySuffix = STREAM_KEY_SUFFIX_MAP[normalizedStream];

  const streamFilter = useMemo(() => {
    if (!(normalizedStream in STREAM_KEY_SUFFIX_MAP)) return undefined;
    if (normalizedStream === 'ib') {
      return (schools: Parameters<typeof filterSecondaryByExam>[0]) =>
        filterSecondaryByExam(schools, 'ib');
    }
    return (schools: Parameters<typeof filterSecondaryByStream>[0]) =>
      filterSecondaryByStream(
        schools,
        normalizedStream as 'g1' | 'g2' | 'g3' | 'ip'
      );
  }, [normalizedStream]);

  const { schools, isLoading, hasLoadError } = useSchoolDirectory({
    schoolType: 'secondary',
    filter: streamFilter,
  });

  // Resolve this stream's content (only its own, not the whole map) and adapt it.
  // Kept unconditional so hooks run every render; an invalid route yields null.
  const layoutContent = useMemo(() => {
    if (!SEC_LEVELS.has(secLevel ?? '') || !streamKeySuffix) return null;
    const content = buildSecondaryStreamContent(secLevel!, normalizedStream);
    if (!content) return null;
    const levelLabel = `Secondary ${secLevel} ${streamKeySuffix}`;
    return toLevelPageLayoutContent(content, { levelLabel });
  }, [secLevel, normalizedStream, streamKeySuffix]);

  if (!layoutContent) {
    return <Navigate to="/sec-1" replace />;
  }

  const directoryHeading =
    STREAM_DIRECTORY_HEADINGS[normalizedStream] ??
    'Secondary Schools in Singapore';

  return (
    <LevelPageLayout content={layoutContent}>
      <SchoolDirectory
        schools={schools}
        heading={directoryHeading}
        isLoading={isLoading}
        emptyStateMessage={
          hasLoadError
            ? 'Unable to load schools right now.'
            : 'No schools found for this zone.'
        }
      />
    </LevelPageLayout>
  );
}

export default SecondaryStreamPage;
